import { useState, useEffect } from 'react';
import { ListChecks, FlaskConical, Check, Beaker as BeakerIcon, Clock, Droplets } from 'lucide-react';
import { useLab } from '../../../context/LabContext';
import { TestTube, Dropper, Timer } from '../../lab';
import InfoBox from '../InfoBox';

const steps = [
  { id: 1, title: 'Prepare Blank Solution', short: 'Click 0 ppm flask' },
  { id: 2, title: 'Prepare Standard Solutions', short: 'Click each standard flask' },
  { id: 3, title: 'Prepare Unknown Sample', short: 'Click unknown flask' },
  { id: 4, title: 'Add Color Reagent', short: 'Click reagent dropper' },
  { id: 5, title: 'Mix Solutions', short: 'Click to mix all' },
  { id: 6, title: 'Start Color Development', short: 'Start 20-min timer' },
  { id: 7, title: 'Observe Color Development', short: 'Watch colors develop' }
];

// Info content for steps
const stepInfoContent: Record<number, { title: string; content: string; chemicalEquation?: string }> = {
  1: {
    title: 'Why a Blank?',
    content: 'The blank contains all reagents except nitrite and is used to zero the spectrophotometer. This corrects for any absorbance from the reagents themselves.'
  },
  2: {
    title: 'Standard Preparation',
    content: 'Standards are prepared by diluting a 10 ppm NO2- stock solution into 100 mL volumetric flasks. For example, 5.0 mL of 10 ppm stock in 100 mL total gives 0.5 ppm. Pour into properly labelled Erlenmeyer flasks.'
  },
  4: {
    title: 'The Griess Reaction',
    content: 'Discovered in 1858 by Peter Griess, this reaction is still the standard method for nitrite detection. Nitrite reacts with sulfanilamide to form a diazonium compound, which then couples with NED to form a pink-red azo dye.',
    chemicalEquation: 'NO2- + Sulfanilamide + NED -> Pink-red azo dye'
  },
  6: {
    title: 'Why 20 Minutes?',
    content: 'Color development takes time because the coupling reaction is not instantaneous. Measuring too early gives low results. The full color develops in about 20 minutes and remains stable for several hours.'
  }
};

// Calculate color intensity based on concentration
const getColorIntensity = (ppm: number, developed: boolean): string => {
  if (!developed) return '#FFFFFF';
  // Pink azo dye color: #FF6478
  const maxPpm = 1.0;
  const intensity = Math.min(ppm / maxPpm, 1);
  // Interpolate from white to pink
  const r = Math.round(255);
  const g = Math.round(255 - (255 - 100) * intensity);
  const b = Math.round(255 - (255 - 120) * intensity);
  return `rgb(${r}, ${g}, ${b})`;
};

const Part1Preparation = () => {
  const { state, updateExp8Part1, completeStep, update } = useLab();
  const { exp8Part1, currentStep, labMode, completedSteps } = state;

  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [dropping, setDropping] = useState(false);
  const [mixing, setMixing] = useState(false);

  // Fix: Reset currentStep to first incomplete step when this part mounts
  useEffect(() => {
    if (labMode !== 'guided') return;
    const firstIncomplete = steps.find(s => !completedSteps.includes(`preparation-${s.id}`));
    const targetStep = firstIncomplete ? firstIncomplete.id : steps[steps.length - 1].id;
    if (currentStep !== targetStep) {
      update({ currentStep: targetStep });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isActive = (s: number) => labMode === 'free' || currentStep === s;
  const isDone = (s: number) => completedSteps.includes(`preparation-${s}`);

  // Timer effect for color development
  useEffect(() => {
    if (!exp8Part1.timerRunning || exp8Part1.colorDevelopmentProgress >= 100) return;

    const timer = setInterval(() => {
      const newProgress = Math.min(exp8Part1.colorDevelopmentProgress + 1, 100);
      updateExp8Part1({ colorDevelopmentProgress: newProgress });

      if (newProgress >= 100) {
        // Color development complete
        const developedStandards = exp8Part1.standards.map((s: { ppm: number; prepared: boolean; colorDeveloped: boolean; absorbance: number }) => ({
          ...s,
          colorDeveloped: s.prepared
        }));
        updateExp8Part1({
          standards: developedStandards,
          unknown: { ...exp8Part1.unknown, colorDeveloped: exp8Part1.unknown.prepared },
          timerRunning: false,
          timerValue: 1200 // 20 minutes in seconds (for display)
        });
        completeStep('preparation-6');
        completeStep('preparation-7');
      }
    }, 50); // Fast for demo (would be 12000ms for real 20 minutes)

    return () => clearInterval(timer);
  }, [exp8Part1.timerRunning, exp8Part1.colorDevelopmentProgress]);

  // Handler: Prepare blank (0 ppm)
  const handlePrepareBlank = () => {
    if (!isActive(1)) return;
    const newStandards = [...exp8Part1.standards];
    newStandards[0] = { ...newStandards[0], prepared: true };
    updateExp8Part1({ standards: newStandards });
    completeStep('preparation-1');
  };

  // Handler: Prepare standards
  const handlePrepareStandard = (index: number) => {
    if (!isActive(2) || index === 0) return;
    const newStandards = [...exp8Part1.standards];
    newStandards[index] = { ...newStandards[index], prepared: true };
    updateExp8Part1({ standards: newStandards });

    // Check if all standards are prepared
    const allPrepared = newStandards.every((s: { prepared: boolean }) => s.prepared);
    if (allPrepared) {
      completeStep('preparation-2');
    }
  };

  // Handler: Prepare unknown
  const handlePrepareUnknown = () => {
    if (!isActive(3) || !exp8Part1.standards.every((s: { prepared: boolean }) => s.prepared)) return;
    updateExp8Part1({ unknown: { ...exp8Part1.unknown, prepared: true } });
    completeStep('preparation-3');
  };

  // Handler: Add color reagent
  const handleAddReagent = () => {
    if (!isActive(4) || !exp8Part1.unknown.prepared) return;
    setDropping(true);
    setTimeout(() => {
      setDropping(false);
      updateExp8Part1({ reagentAdded: true });
      completeStep('preparation-4');
    }, 800);
  };

  // Handler: Mix solutions
  const handleMix = () => {
    if (!isActive(5) || !exp8Part1.reagentAdded) return;
    setMixing(true);
    setTimeout(() => {
      setMixing(false);
      completeStep('preparation-5');
    }, 1500);
  };

  // Handler: Start timer
  const handleStartTimer = () => {
    if (!isActive(6) || !isDone(5)) return;
    updateExp8Part1({ timerRunning: true, timerValue: 0 });
  };

  const completedCount = completedSteps.filter((s: string) => s.startsWith('preparation')).length;

  return (
    <div className="part1-preparation">
      {/* Steps Panel */}
      <div className="steps-panel">
        <div className="steps-header">
          <ListChecks size={16} />
          <h3>Part I: Preparation</h3>
        </div>
        <div className="steps-list">
          {steps.map(s => (
            <div
              key={s.id}
              className={`step-item ${isActive(s.id) && !isDone(s.id) ? 'active' : ''}`}
            >
              <div className={`step-number ${isDone(s.id) ? 'done' : isActive(s.id) ? 'current' : ''}`}>
                {isDone(s.id) ? <Check size={12} /> : s.id}
              </div>
              <div className="step-content">
                <div className="step-title-row">
                  <span className={`step-title ${isActive(s.id) ? 'highlight' : ''}`}>
                    {s.title}
                  </span>
                  {stepInfoContent[s.id] && (
                    <button
                      className="step-info-btn"
                      onClick={(e) => { e.stopPropagation(); setShowInfo(s.id); }}
                    >
                      ?
                    </button>
                  )}
                </div>
                {isActive(s.id) && !isDone(s.id) && (
                  <span className="step-hint">{s.short}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lab Bench */}
      <div className="lab-bench">
        <div className="bench-header">
          <div className="bench-title">
            <FlaskConical size={16} />
            <h3>Lab Bench - Nitrite Standard Preparation</h3>
          </div>
          <span className="bench-progress">{completedCount}/7 Complete</span>
        </div>

        <div className="bench-area">
          {/* Color Development Progress */}
          {exp8Part1.colorDevelopmentProgress > 0 && (
            <div className="development-progress">
              <Clock size={16} />
              <span>Color Development:</span>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${exp8Part1.colorDevelopmentProgress}%` }}
                />
              </div>
              <span>{Math.round(exp8Part1.colorDevelopmentProgress)}%</span>
            </div>
          )}

          {/* Standards Row */}
          <div className="standards-section">
            <h4>Standard Solutions</h4>
            <div className="test-tubes-row">
              {exp8Part1.standards.map((std: { ppm: number; prepared: boolean; colorDeveloped: boolean }, index: number) => (
                <div key={index} className="tube-container">
                  <TestTube
                    fillLevel={std.prepared ? 70 : 0}
                    fillColor={getColorIntensity(std.ppm, std.colorDeveloped)}
                    label={`${std.ppm} ppm`}
                    onClick={index === 0 ? handlePrepareBlank : () => handlePrepareStandard(index)}
                    active={
                      (index === 0 && isActive(1) && !std.prepared) ||
                      (index > 0 && isActive(2) && !std.prepared && exp8Part1.standards[0].prepared)
                    }
                    disabled={labMode === 'guided' && !isActive(index === 0 ? 1 : 2)}
                    hasResult={std.colorDeveloped}
                  />
                  {std.prepared && !std.colorDeveloped && exp8Part1.reagentAdded && (
                    <div className="reagent-indicator">
                      <Droplets size={12} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Unknown Sample */}
          <div className="unknown-section">
            <h4>Unknown Sample</h4>
            <div className="tube-container unknown-tube">
              <TestTube
                fillLevel={exp8Part1.unknown.prepared ? 70 : 0}
                fillColor={getColorIntensity(0.35, exp8Part1.unknown.colorDeveloped)} // Unknown ~ 0.35 ppm
                label="Unknown"
                onClick={handlePrepareUnknown}
                active={isActive(3) && !exp8Part1.unknown.prepared}
                disabled={labMode === 'guided' && !isActive(3)}
                hasResult={exp8Part1.unknown.colorDeveloped}
              />
              {exp8Part1.unknown.prepared && !exp8Part1.unknown.colorDeveloped && exp8Part1.reagentAdded && (
                <div className="reagent-indicator">
                  <Droplets size={12} />
                </div>
              )}
            </div>
          </div>

          {/* Reagents and Timer */}
          <div className="tools-section">
            <Dropper
              label="Marshall's Reagent"
              liquidColor="#FFE4B5"
              onClick={handleAddReagent}
              active={isActive(4) && !exp8Part1.reagentAdded}
              disabled={labMode === 'guided' && !isActive(4)}
              dropping={dropping}
            />

            <div className="mix-button-container">
              <button
                className={`mix-btn ${isActive(5) && !isDone(5) && exp8Part1.reagentAdded ? 'active' : ''} ${mixing ? 'mixing' : ''}`}
                onClick={handleMix}
                disabled={labMode === 'guided' && !isActive(5)}
              >
                {mixing ? 'Mixing...' : 'Mix All'}
              </button>
            </div>

            <Timer
              time={Math.round(exp8Part1.colorDevelopmentProgress * 12)} // Simulated seconds
              running={exp8Part1.timerRunning}
              onClick={handleStartTimer}
            />
          </div>

          {/* Reaction Info */}
          {exp8Part1.reagentAdded && !exp8Part1.timerRunning && exp8Part1.colorDevelopmentProgress === 0 && isDone(5) && (
            <div className="reaction-info">
              <BeakerIcon size={20} />
              <span>Marshall's reagent added. Start timer for color development.</span>
            </div>
          )}

          {exp8Part1.colorDevelopmentProgress >= 100 && (
            <div className="completion-message">
              <Check size={20} />
              <span>Color development complete! Ready for spectrophotometric measurement.</span>
            </div>
          )}

          {/* Bench Surface */}
          <div className="bench-surface" />
        </div>
      </div>

      {/* Info Box */}
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp8"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}

      <style>{`
        .part1-preparation {
          display: flex;
          gap: 16px;
          flex: 1;
          width: 100%;
          min-height: 0;
          padding: 16px;
        }

        .steps-panel {
          width: 240px;
          background: var(--bg-surface);
          border-radius: 12px;
          border: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .steps-header {
          background: linear-gradient(135deg, var(--primary), var(--primary-dark));
          padding: 14px 16px;
          display: flex;
          align-items: center;
          gap: 10px;
          color: white;
        }

        .steps-header h3 {
          font-size: 14px;
          font-weight: 600;
        }

        .steps-list {
          flex: 1;
          overflow-y: auto;
          padding: 12px;
        }

        .step-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 10px 12px;
          border-radius: 8px;
          margin-bottom: 4px;
          transition: background 0.15s;
        }

        .step-item.active {
          background: rgba(255, 90, 54, 0.15);
        }

        .step-number {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 600;
          flex-shrink: 0;
          background: var(--border);
          color: var(--text-muted);
        }

        .step-number.done {
          background: var(--secondary);
          color: white;
        }

        .step-number.current {
          background: var(--primary);
          color: white;
        }

        .step-content {
          flex: 1;
          min-width: 0;
        }

        .step-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .step-title {
          font-size: 12px;
          font-weight: 600;
        }

        .step-title.highlight {
          color: var(--primary-light);
        }

        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
          border: none;
          cursor: pointer;
        }

        .step-info-btn:hover {
          transform: scale(1.1);
        }

        .step-hint {
          font-size: 11px;
          color: var(--text-secondary);
          margin-top: 4px;
          display: block;
        }

        .lab-bench {
          flex: 1;
          background: var(--bg-surface);
          border-radius: 12px;
          border: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          overflow: auto;
        }

        .bench-header {
          background: linear-gradient(135deg, var(--secondary), var(--secondary-dark));
          padding: 12px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: white;
        }

        .bench-title {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .bench-title h3 {
          font-size: 14px;
          font-weight: 600;
        }

        .bench-progress {
          font-size: 12px;
          opacity: 0.9;
        }

        .bench-area {
          flex: 1;
          background: linear-gradient(180deg, var(--bg-elevated) 0%, var(--bg-surface) 100%);
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 24px;
          gap: 24px;
          overflow-y: auto;
        }

        .development-progress {
          display: flex;
          align-items: center;
          gap: 12px;
          background: var(--bg-dark);
          padding: 12px 20px;
          border-radius: 10px;
          width: 100%;
          max-width: 500px;
        }

        .development-progress .progress-bar {
          flex: 1;
          height: 8px;
          background: var(--border);
          border-radius: 4px;
          overflow: hidden;
        }

        .development-progress .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #FF6478, #FF8090);
          transition: width 0.3s;
        }

        .development-progress span {
          font-size: 12px;
          color: var(--text-secondary);
        }

        .standards-section, .unknown-section {
          text-align: center;
        }

        .standards-section h4, .unknown-section h4 {
          font-size: 13px;
          color: var(--text-secondary);
          margin-bottom: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .test-tubes-row {
          display: flex;
          gap: 20px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .tube-container {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .unknown-tube {
          margin-top: 8px;
        }

        .reagent-indicator {
          position: absolute;
          top: -8px;
          right: -8px;
          background: var(--accent);
          border-radius: 50%;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--bg-dark);
          animation: pulse 1s infinite;
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }

        .tools-section {
          display: flex;
          gap: 32px;
          align-items: flex-end;
          margin-top: 16px;
        }

        .mix-button-container {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .mix-btn {
          padding: 12px 24px;
          background: var(--bg-elevated);
          border: 2px solid var(--border);
          border-radius: 10px;
          color: var(--text-secondary);
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .mix-btn.active {
          border-color: var(--primary);
          color: var(--primary);
          animation: glow 1.5s ease-in-out infinite;
        }

        .mix-btn.mixing {
          background: var(--primary);
          color: white;
          animation: shake 0.5s infinite;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-3px); }
          75% { transform: translateX(3px); }
        }

        @keyframes glow {
          0%, 100% { box-shadow: 0 0 5px rgba(255, 90, 54, 0.3); }
          50% { box-shadow: 0 0 15px rgba(255, 90, 54, 0.5); }
        }

        .reaction-info, .completion-message {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 20px;
          border-radius: 10px;
          font-size: 13px;
          max-width: 500px;
        }

        .reaction-info {
          background: rgba(255, 234, 0, 0.15);
          color: var(--accent);
        }

        .completion-message {
          background: rgba(0, 168, 107, 0.15);
          color: var(--secondary);
        }

        .bench-surface {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default Part1Preparation;
